import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  HOUSEHOLD_ROLES,
  type HouseholdDetailResponse,
  type HouseholdRole,
} from '@tabita/contracts';
import { useAuth } from '@/auth/auth-context';
import { useI18n } from '@/i18n/i18n-provider';
import { peopleApi } from '@/services/people.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, Field } from '@/components/ui';
import { PersonPicker } from './person-picker';

/**
 * A person's household — at most one at a time in this model (see
 * `HouseholdRepository.findForPerson`). Shown on the Info tab as its own
 * card, mirroring the roster pattern already established on a Group's page.
 */
export function HouseholdCard({
  personId,
  household,
  onChanged,
}: {
  readonly personId: string;
  readonly household: HouseholdDetailResponse | null;
  readonly onChanged: () => void;
}) {
  const { t } = useI18n();
  const { can } = useAuth();
  const [error, setError] = useState<string | null>(null);

  const addMember = useMutation({
    mutationFn: (input: { householdId: string; targetPersonId: string; role: HouseholdRole }) =>
      peopleApi.addHouseholdMember(input.householdId, {
        personId: input.targetPersonId,
        role: input.role,
        isPrimaryContact: false,
      }),
    onSuccess: () => {
      setError(null);
      onChanged();
    },
    onError: (err: unknown) => setError(describeError(err, t)),
  });

  const removeMember = useMutation({
    mutationFn: (targetPersonId: string) =>
      peopleApi.removeHouseholdMember(household!.id, targetPersonId),
    onSuccess: () => {
      setError(null);
      onChanged();
    },
    onError: (err: unknown) => setError(describeError(err, t)),
  });

  if (!can('people.household.read')) return null;

  return (
    <section className="detail-section">
      <h2 className="detail-section-title">{t('people.household.title')}</h2>

      {error && <Alert tone="danger">{error}</Alert>}

      {household ? (
        <div>
          <p className="settings-status-title">{household.name}</p>
          <ul className="people-relation-list">
            {household.members.map((member) => (
              <li key={member.personId} className="member-row">
                <Link to={`/people/${member.personId}`}>{member.displayName}</Link>
                <span className="cell-mono">{t(`people.household.role.${member.role}`)}</span>
                {can('people.household.update') && member.personId !== personId && (
                  <Button
                    variant="ghost"
                    loading={removeMember.isPending && removeMember.variables === member.personId}
                    onClick={() => removeMember.mutate(member.personId)}
                  >
                    {t('people.household.remove')}
                  </Button>
                )}
              </li>
            ))}
          </ul>

          {can('people.household.update') && (
            <AddHouseholdMemberForm
              existing={household.members.map((m) => m.personId)}
              onAdd={(targetPersonId, role) =>
                addMember.mutate({ householdId: household.id, targetPersonId, role })
              }
            />
          )}
        </div>
      ) : (
        can('people.household.create') && (
          <CreateHouseholdForm personId={personId} onCreated={onChanged} />
        )
      )}
    </section>
  );
}

function AddHouseholdMemberForm({
  existing,
  onAdd,
}: {
  readonly existing: readonly string[];
  readonly onAdd: (personId: string, role: HouseholdRole) => void;
}) {
  const { t } = useI18n();
  const [role, setRole] = useState<HouseholdRole>('OTHER');

  return (
    <div className="form-grid">
      <div className="field">
        <label className="field-label" htmlFor="household-member-role">
          {t('people.household.roleLabel')}
        </label>
        <select
          id="household-member-role"
          className="field-input"
          value={role}
          onChange={(event) => setRole(event.target.value as HouseholdRole)}
        >
          {HOUSEHOLD_ROLES.map((value) => (
            <option key={value} value={value}>
              {t(`people.household.role.${value}`)}
            </option>
          ))}
        </select>
      </div>
      <PersonPicker
        label={t('people.household.addMember')}
        placeholder={t('groups.group.choosePerson')}
        exclude={existing}
        onPick={(personId) => onAdd(personId, role)}
      />
    </div>
  );
}

function CreateHouseholdForm({
  personId,
  onCreated,
}: {
  readonly personId: string;
  readonly onCreated: () => void;
}) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: async (householdName: string) => {
      const created = await peopleApi.createHousehold({ name: householdName });
      await peopleApi.addHouseholdMember(created.id, {
        personId,
        role: 'HEAD',
        isPrimaryContact: true,
      });
      return created;
    },
    onSuccess: async () => {
      setError(null);
      setName('');
      await queryClient.invalidateQueries({ queryKey: ['people'], exact: false });
      onCreated();
    },
    onError: (err: unknown) => setError(describeError(err, t)),
  });

  return (
    <form
      className="people-inline-form"
      onSubmit={(event: FormEvent) => {
        event.preventDefault();
        if (name.trim()) create.mutate(name.trim());
      }}
    >
      {error && <Alert tone="danger">{error}</Alert>}
      <Field
        label={t('people.household.name')}
        name="household-name"
        value={name}
        onChange={(event) => setName(event.target.value)}
        placeholder={t('people.household.namePlaceholder')}
      />
      <Button type="submit" variant="primary" loading={create.isPending} disabled={!name.trim()}>
        {t('people.household.create')}
      </Button>
    </form>
  );
}

function describeError(error: unknown, t: ReturnType<typeof useI18n>['t']): string {
  return error instanceof ApiError
    ? t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
    : t('errors.INTERNAL_ERROR');
}
